import React, { useEffect, useState } from 'react';
import styles from './furnishingContainer.module.scss';
import { PackagesTabContainer } from './components/PackagesTabContainer';
import { WEBSITE_HOME } from '../shared/config/routes-config';
import {
  getAllFurnishingPackages,
  getAllModels,
  getAllOffers,
  getAllShowRooms,
  getAllItemSpecs,
  getAllAddOns
} from './furnishing.service';
import type {
  FurnishingOffer,
  ShowRoomResponse,
  ItemSpecResponse,
  AddOnResponse
} from './furnishing.service';
import type { ErrorInfo } from '../shared/interfaces/error-info.interface';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';
import type { FurnishingPackage, PackageModel, FurnishingModel } from './types';
import { PortfolioTabContainer } from './components/PortfolioTabContainer';
import { FurnishingUnitTypesProvider } from './FurnishingUnitTypesContext';
import { RootState } from '../shared/redux/reducers';
import { connect } from 'react-redux';

type TabType = 'packages' | 'portfolio';

export const FurnishingManagementContainer: React.FC<ReturnType<typeof mapStateToProps>> = ({
  isAuthenticated
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('packages');
  const [packages, setPackages] = useState<FurnishingPackage[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const handlePackagesChange = (newPackages: FurnishingPackage[]) => {
    setPackages(newPackages);
  };

  useEffect(() => {
    Promise.all([
      getAllFurnishingPackages(),
      getAllModels(),
      getAllOffers(),
      getAllShowRooms(),
      getAllItemSpecs(),
      getAllAddOns()
    ]).then(
      ([packagesRes, modelsRes, offersRes, showRoomsRes, itemSpecsRes, addOnsRes]: [
        PackageModel[] | ErrorInfo,
        FurnishingModel[] | ErrorInfo,
        FurnishingOffer[] | ErrorInfo,
        ShowRoomResponse[] | ErrorInfo,
        ItemSpecResponse[] | ErrorInfo,
        AddOnResponse[] | ErrorInfo
      ]) => {
        if (!isErrorInfo(packagesRes)) {
          const models = !isErrorInfo(modelsRes) ? modelsRes : [];
          const offers = !isErrorInfo(offersRes) ? offersRes : [];
          const showRooms = !isErrorInfo(showRoomsRes) ? showRoomsRes : [];
          const itemSpecs = !isErrorInfo(itemSpecsRes) ? itemSpecsRes : [];
          const addOns = !isErrorInfo(addOnsRes) ? addOnsRes : [];

          const mappedPackages: FurnishingPackage[] = packagesRes.map((pkg) => {
            const packageModels = models
              .filter((model: FurnishingModel) => model.packageId === pkg.id)
              .map((model: FurnishingModel) => {
                const modelPrices = offers
                  .filter(
                    (offer: FurnishingOffer) =>
                      offer.modelId === model.id && offer.packageId === pkg.id
                  )
                  .map((offer: FurnishingOffer) => ({
                    id: offer.id,
                    unitTypeGroupId: String(offer.unitTypeGroupId || ''),
                    price: offer.price,
                    addons: offer.addons
                  }));

                const modelShowRooms = showRooms
                  .filter((room: ShowRoomResponse) => room.modelId === model.id)
                  .map((room: ShowRoomResponse) => {
                    const roomItemSpecs = itemSpecs
                      .filter((spec: ItemSpecResponse) => spec.showRoomId === room.id)
                      .map((spec: ItemSpecResponse) => ({
                        id: spec.id,
                        name: spec.name,
                        nameAr: spec.nameAr,
                        description: spec.description,
                        descriptionAr: spec.descriptionAr,
                        image: spec.image,
                        order: spec.order
                      }));

                    return {
                      id: room.id,
                      name: room.name,
                      nameAr: room.nameAr,
                      images: room.images || [],
                      itemSpecs: roomItemSpecs,
                      order: room.order
                    };
                  });

                return {
                  id: model.id || '',
                  name: model.name || '',
                  nameAr: model.nameAr || '',
                  color: model.color || '#F5A623',
                  image: model.image || '',
                  showRooms: modelShowRooms,
                  prices: modelPrices,
                  order: model.order
                };
              });

            return {
              id: pkg.id,
              image: pkg.image || '',
              name: pkg.name || '',
              nameAr: pkg.nameAr || '',
              descriptionEn: pkg.description || '',
              descriptionAr: pkg.descriptionAr || '',
              models: packageModels,
              order: pkg.order,
              addOns: addOns.filter(
                (group: AddOnResponse) => String(group.packageId) === String(pkg.id)
              )
            };
          });

          setPackages(mappedPackages);
        }
        setIsLoading(false);
      }
    );
  }, []);

  const packageDropdownItems = packages.map((pkg) => ({ value: pkg.id || '', label: pkg.name }));
  const modelsDropdownItems = packages
    .flatMap((pkg) =>
      (pkg?.models ?? []).map((model) => ({
        value: model.id || '',
        label: model.name
      }))
    )
    .filter((item): item is { value: string; label: string } => !!item.value && !!item.label);

  return (
    <FurnishingUnitTypesProvider>
      <section className={styles['furnishing-management']}>
        <div className={styles['furnishing-management__tabs']}>
          <button
            type="button"
            className={`${styles['furnishing-management__tab']} ${
              activeTab === 'packages' ? styles['furnishing-management__tab--active'] : ''
            }`}
            onClick={() => setActiveTab('packages')}
          >
            Packages
          </button>
          <button
            type="button"
            className={`${styles['furnishing-management__tab']} ${
              activeTab === 'portfolio' ? styles['furnishing-management__tab--active'] : ''
            }`}
            onClick={() => setActiveTab('portfolio')}
          >
            Portfolio
          </button>
        </div>
        <div className={styles['furnishing-management__welcome-banner']}>
          <span>👋</span>
          <div
            style={{
              textAlign: 'center'
            }}
          >
            <p>
              Hello design team! Please verify your changes on the{' '}
              <a href={`${WEBSITE_HOME}packages`} target="_blank" rel="noopener noreferrer">
                Furnishing website
              </a>{' '}
              to ensure everything works as expected.
            </p>
          </div>
        </div>
        {activeTab === 'packages' && (
          <PackagesTabContainer
            packages={packages}
            onPackagesChange={handlePackagesChange}
            isLoading={isLoading}
          />
        )}
        {activeTab === 'portfolio' && (
          <PortfolioTabContainer packages={packageDropdownItems} models={modelsDropdownItems} />
        )}
      </section>
    </FurnishingUnitTypesProvider>
  );
};

export default connect(mapStateToProps)(FurnishingManagementContainer);
/**
 * function to to map redux state to component props
 * @param state root state or redux
 */
function mapStateToProps(state: RootState) {
  return {
    isAuthenticated: !!state.auth.accessToken
  };
}
